import React from "react";
import { useRef } from "react";
import Footer from "../components/Footer/PrelaunchFooter/Footer";
import Navbar from "../components/Navbar/Navbar";

const PrivacyPolicy = () => {
  const aboutUsRef = useRef(null);
  const ourProcessRef = useRef(null);
  const manufactureRef = useRef(null);
  const ingredientsRef = useRef(null);

  return (
    <div style={styles.pageContainer}>
      <div style={styles.navbar}>
        <Navbar
          aboutUsRef={aboutUsRef}
          ourProcessRef={ourProcessRef}
          manufactureRef={manufactureRef}
          ingredientsRef={ingredientsRef}
        />
      </div>
      <div style={styles.contentContainer}>
        <h1 style={styles.title}>Privacy Policy</h1>
        <p style={styles.subtitle}>Last Updated: February 14, 2025</p>
        <div style={styles.textContainer}>
          <p>
            This Privacy Policy describes how Frissly (the "Site", "we", "us",
            or "our") collects, uses, and discloses your personal information
            when you visit, use our services, or make a purchase from
            www.frissly.com (the "Site") or otherwise communicate with us
            regarding the Site (collectively, the "Services"). For purposes of
            this Privacy Policy, "you" and "your" means you as the user of the
            Services, whether you are a customer, website visitor, or another
            individual whose information we collect. By accessing or using our
            Services, you agree to the collection, use, and disclosure of your
            information as described in this Privacy Policy. If you do not
            agree, please do not use our Services.
          </p>
          <div style={styles.divider}></div>
          <h2 style={styles.sectionTitle}>Changes to This Privacy Policy</h2>
          <p>
            We may update this Privacy Policy periodically to reflect changes in
            our data practices, legal obligations, or business needs. We will
            notify you of material changes by posting the revised policy with
            the updated date. Your continued use of the Site after changes
            constitutes acceptance of the revised Privacy Policy.
          </p>
          <div style={styles.divider}></div>
          <h2 style={styles.sectionTitle}>
            How We Collect and Use Your Personal Information
          </h2>
          <p>
            We collect personal information from you in multiple ways to provide
            our Services efficiently.
          </p>
          <h3 style={styles.subSectionTitle}>
            1. Information We Collect Directly from You
          </h3>
          <p>
            When you use our Services, we may collect the following personal
            information:
          </p>
          <ul style={styles.list}>
            <li>
              <strong>Contact details:</strong> Name, address, phone number, and
              email.
            </li>
            <li>
              <strong>Order details:</strong> Billing and shipping address,
              payment confirmation, email, and phone number.
            </li>
            <li>
              <strong>Account details:</strong> Username, password, security
              questions, and preferences.
            </li>
            <li>
              <strong>Shopping activity:</strong> Products viewed, added to
              cart, purchased, loyalty points, referrals, gift cards, and
              reviews.
            </li>
            <li>
              <strong>Customer support interactions:</strong> Communications
              with us via chat, email, or phone.
            </li>
          </ul>
          <p>
            Some features may require you to provide personal information. If
            you choose not to, you may be unable to access those features.
          </p>
          <div style={styles.divider}></div>
          <h3 style={styles.subSectionTitle}>
            2. Information We Collect Automatically
          </h3>
          <p>
            We collect Usage Data through cookies, web beacons, and tracking
            technologies, including:
          </p>
          <ul style={styles.list}>
            <li>
              <strong>Device Information:</strong> Browser type, IP address,
              operating system.
            </li>
            <li>
              <strong>Network Information:</strong> Internet connection details.
            </li>
            <li>
              <strong>Interaction Data:</strong> How you browse and interact
              with our App, Site, and services.
            </li>
            <li>
              <strong>Location:</strong> We may collect your device's approximate or precise location to check whether your area is serviceable and to assist in suggesting accurate delivery addresses.
            </li>
          </ul>
          <div style={styles.divider}></div>
          <h3 style={styles.subSectionTitle}>
            3. Information from Third Parties
          </h3>
          <p>
            We may receive personal information from third-party vendors, such
            as:
          </p>
          <ul style={styles.list}>
            <li>
              <strong>
                Payment Processors (Razorpay & CCAvenue, managed by Juspay):
              </strong>{" "}
              For processing payments securely.
            </li>
            <li>
              <strong>Shipping & Logistics Providers:</strong> To fulfill
              orders.
            </li>
            <li>
              <strong>Marketing & Analytics Providers:</strong> To improve our
              app, website, and advertisements.
            </li>
            <li>
              <strong>Fraud Prevention Partners:</strong> To detect and prevent
              fraudulent activity.
            </li>
          </ul>
          <div style={styles.divider}></div>
          <h2 style={styles.sectionTitle}>
            How We Use Your Personal Information
          </h2>
          <p>We use your information for:</p>
          <ul style={styles.list}>
            <li>
              <strong>Providing Products & Services:</strong> To process orders,
              handle payments, send order updates, and manage your account.
            </li>
            <li>
              <strong>Customer Support & Service Improvement:</strong>{" "}
              Responding to inquiries, troubleshooting issues, and enhancing
              user experience.
            </li>
            <li>
              <strong>Marketing & Promotions:</strong> Sending newsletters,
              promotional offers, and targeted advertisements. You can opt out
              at any time.
            </li>
            <li>
              <strong>Security & Fraud Prevention:</strong> Detecting
              unauthorized transactions and ensuring compliance with legal
              obligations.
            </li>
            <li>
              <strong>Compliance & Legal Obligations:</strong> Meeting
              regulatory requirements and enforcing terms of service.
            </li>
          </ul>
          <div style={styles.divider}></div>
          <h2 style={styles.sectionTitle}>Cookies & Tracking Technologies</h2>
          <p>We use cookies and tracking technologies to:</p>
          <ul style={styles.list}>
            <li>Enhance website and app functionality.</li>
            <li>Improve user experience.</li>
            <li>Provide personalized content and ads.</li>
          </ul>
          <p>
            <strong>
              You can manage or disable cookies through your browser settings.
              Blocking cookies may affect website functionality.
            </strong>
          </p>

          <div style={styles.divider}></div>
          <h2 style={styles.sectionTitle}>How We Share Personal Information</h2>
          <p>We may disclose personal information in the following cases:</p>
          <ul style={styles.list}>
            <li>
              <strong>With Service Providers:</strong> Payment processors,
              logistics, and analytics providers.
            </li>
            <li>
              <strong>With Business & Marketing Partners:</strong> For
              advertising and promotional activities.
            </li>
            <li>
              <strong>For Legal Compliance:</strong> If required by law
              enforcement or regulatory authorities.
            </li>
            <li>
              <strong>In Business Transfers:</strong> If we undergo a merger,
              acquisition, or sale of assets.
            </li>
          </ul>
          <p>
            We do not sell or share sensitive personal data without consent.
          </p>
          <div style={styles.divider}></div>
          <h2 style={styles.sectionTitle}>Third-Party Links</h2>
          <p>
            Our Site may contain links to third-party websites. We are not
            responsible for their privacy policies or practices. Please review
            their policies before providing personal information.
          </p>
          <h2 style={styles.sectionTitle}>Children’s Privacy</h2>
          <p>
            Our Services are not intended for children under 18. We do not
            knowingly collect personal information from minors. If you believe a
            child has provided us information, contact us to request deletion.
          </p>
          <div style={styles.divider}></div>
          <h2 style={styles.sectionTitle}>Data Security & Retention</h2>
          <ul style={styles.list}>
            <li>
              We use industry-standard security measures to protect your data.
            </li>
            <li>
              However, no online platform is 100% secure. Avoid sharing
              sensitive details through unsecured channels.
            </li>
            <li>
              We retain personal information only as long as necessary to
              provide services and comply with legal obligations.
            </li>
          </ul>
          <div style={styles.divider}></div>
          <h2 style={styles.sectionTitle}>Your Rights</h2>
          <p>
            Depending on applicable laws, you may have the following rights:
          </p>
          <ul style={styles.list}>
            <li>
              <strong>Right to Access:</strong> Request details on how we use
              your data.
            </li>
            <li>
              <strong>Right to Correct:</strong> Update inaccurate personal
              information.
            </li>
            <li>
              <strong>Right to Delete:</strong> Request deletion of your data
              (subject to legal limitations).
            </li>
            <li>
              <strong>Right to Opt-Out:</strong> Unsubscribe from marketing
              communications.
            </li>
            <li>
              <strong>Right to Withdraw Consent:</strong> Where applicable,
              withdraw consent for data processing.
            </li>
          </ul>
          <p>To exercise your rights, contact us using the details below.</p>
          <div style={styles.divider}></div>
          <h2 style={styles.sectionTitle}>International Data Transfers</h2>
          <p>
            If you are outside India, your information may be transferred to
            servers in India or other jurisdictions. We use standard security
            protocols for international data transfers.
          </p>
          <h2 style={styles.sectionTitle}>Complaints & Dispute Resolution</h2>
          <p>If you have concerns about our privacy practices, you may:</p>
          <ul style={styles.list}>
            <li>
              <strong>Contact Us:</strong> We will attempt to resolve issues
              promptly.
            </li>
            <li>
              <strong>Lodge a Complaint:</strong> If unsatisfied, you may
              escalate complaints to the appropriate data protection authority.
            </li>
          </ul>
          <div style={styles.divider}></div>
          <h2 style={styles.sectionTitle}>Contact Us</h2>
          <p>For privacy-related inquiries, you can reach us at:</p>
          <ul
            style={{
              listStyleType: "none",
            }}
          >
            <li>
              📧<strong>Email:</strong> support@frissly.com
            </li>
            <li>
              📞<strong>Phone:</strong> +91-9010703332
            </li>
            <li>
              📍<strong>Address:</strong> Survey no 306, Divya Farms,
              Gundlapochampally, Gandimaisamma, Hyderabad, India
            </li>
          </ul>
        </div>
      </div>
      <Footer />
    </div>
  );
};

const styles = {
  pageContainer: {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    backgroundColor: "#f5f5f5",
  },
  navbar: {
    backgroundColor: "#ffffff",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
  },
  contentContainer: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "20px",
    maxWidth: "800px",
    margin: "0 auto",
  },
  title: {
    fontFamily: "Nunito, sans-serif",
    fontSize: "32px",
    fontWeight: "bold",
    color: "#000000",
    marginBottom: "10px",
    textAlign: "center",
  },
  subtitle: {
    fontFamily: "Nunito, sans-serif",
    fontSize: "18px",
    color: "#555555",
    marginBottom: "20px",
    textAlign: "center",
  },
  textContainer: {
    fontFamily: "Nunito, sans-serif",
    fontSize: "16px",
    lineHeight: "24px",
    color: "#555555",
    textAlign: "left",
    width: "100%",
  },
  sectionTitle: {
    fontFamily: "Nunito, sans-serif",
    fontSize: "24px",
    fontWeight: "bold",
    color: "#000000",
    marginTop: "20px",
    marginBottom: "10px",
  },
  subSectionTitle: {
    fontFamily: "Nunito, sans-serif",
    fontSize: "20px",
    fontWeight: "bold",
    color: "#333333",
    marginTop: "15px",
    marginBottom: "10px",
  },
  list: {
    listStyleType: "disc",
    paddingLeft: "20px",
    marginBottom: "15px",
  },
  divider: {
    borderBottom: "1px solid #cccccc",
    margin: "20px 0",
  },
};

export default PrivacyPolicy;
